import { Content, ShopContentResponse } from "../entities/content";
import { isCountryInOneOfRegions, splitLocale } from "../entities/locale";
import { modifyShopItemDependingOnMarket } from "../utility/helper/shop-helper";
import { NaviError, NaviErrorTypes } from "../utility/navi-error";
import { navStateStore } from "../state/nav-state-store";
import { Application, ApplicationType } from "../utility/constants/app";
import { constructCacheKey, getConfig } from "../utility/helper";
import { Statuscodes } from "../utility/constants/statuscodes";

export const contentCache = new Map<string, Content>();

export async function fetchContent(app?: ApplicationType): Promise<Content> {
  const { locale } = navStateStore.state;
  const config = getConfig(navStateStore.state.env);

  const isShopEnabled = app === Application.shop;
  const contentUrl = config.CONTENT_URL;

  const naviContentPromise = fetchNaviContent(contentUrl, locale);
  const shopContentPromise = isShopEnabled
    ? fetchShopContent(config.SHOP_CONTENT_URL, locale)
    : Promise.resolve(null);

  const [naviContentResult, shopContentResult] = await Promise.allSettled([
    naviContentPromise,
    shopContentPromise,
  ]);

  if (naviContentResult.status === "rejected") {
    throw naviContentResult.reason;
  }

  const naviContent = modifyShopItemDependingOnMarket(
    locale,
    app,
    naviContentResult.value,
  );

  const shopContent =
    shopContentResult.status === "fulfilled" ? shopContentResult.value : null;

  if (shopContent === null || naviContent.shop === null) {
    return naviContent;
  }

  return {
    ...naviContent,
    shop: {
      ...naviContent.shop,
      children: shopContent.menuItems || naviContent.shop?.children || [],
      additionalContent: shopContent.additionalContent,
      availableLocales: naviContent.shop.availableLocales,
      availableLocalesOnlyForShop: naviContent.shop.availableLocalesOnlyForShop,
    },
  };
}

async function fetchNaviContent(
  contentUrl: string,
  locale: string,
): Promise<Content> {
  const { country, language } = splitLocale(locale);
  const cacheKey = constructCacheKey(contentUrl, locale);
  const cachedContent = contentCache.get(cacheKey);

  if (cachedContent !== undefined) {
    return cachedContent;
  }

  const data = await fetch(`${contentUrl}/${`${language}-${country}`}.json`, {
    headers: {
      "Cache-Control": "no-cache",
      "Content-Type": "application/json",
    },
  });

  if (data.status === Statuscodes.notFound) {
    throw new NaviError(
      `No content for locale ${locale} found`,
      NaviErrorTypes.NO_RESULTS,
    );
  }

  if (data.status === Statuscodes.internalServerError) {
    throw new NaviError(
      `Could not get content for locale ${locale}`,
      NaviErrorTypes.GENERAL,
    );
  }

  const content = (await data.json()) as Content;
  contentCache.set(cacheKey, content);

  return content;
}

async function fetchShopContent(
  contentUrl: string,
  locale: string,
): Promise<ShopContentResponse> {
  const { country, language } = splitLocale(locale);

  const data = await fetch(
    `${contentUrl}/${country.toLowerCase()}/${`${language}-${country}`}/navigation`,
    {
      headers: {
        "Content-Type": "application/json",
        "x-vercel-protection-bypass": "SHOP_CONTENT_KEY",
      },
    },
  );

  if (data.status === Statuscodes.notFound) {
    throw new NaviError(
      `No shop content for locale ${locale} found`,
      NaviErrorTypes.NO_RESULTS,
    );
  }

  if (!data.ok) {
    throw new NaviError(
      `Could not get shop content for locale ${locale}`,
      NaviErrorTypes.GENERAL,
    );
  }

  return data.json();
}

export function constructFlagURL(
  footerAssetsUrl: string,
  locale: string,
): string {
  const { country } = splitLocale(locale);

  let flag = country;
  if (flag.includes("-") && !isCountryInOneOfRegions(flag)) {
    // eslint-disable-next-line prefer-destructuring
    flag = country.split("-")[0];
  }

  return `${footerAssetsUrl}/flags/${flag}.svg`;
}

export async function getFlagURL(
  footerAssetsUrl: string,
  locale: string,
): Promise<string> {
  try {
    const flagURL = constructFlagURL(footerAssetsUrl, locale);

    const res = await fetch(flagURL);

    // Only set flagURL if flag actually exists
    if (res.ok) {
      return flagURL;
    }
  } catch (err) {
    // ignore error
  }

  return "";
}
